/**
 * LaborPage — piece-rate picking labor: daily wage summary (with minimum-wage
 * topup + overtime + CA rest-break pay), per-picker throughput, AI/heuristic
 * crew-mix recommendations, a clock board, and quick tally entry.
 *
 * Backed by /api/v1/labor/* (see internal/labor, CLAUDE.md §93). Wage figures
 * are an operational estimate — the API's disclaimer is surfaced in the UI.
 */
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  DollarSign, Scale, Clock, Users, TrendingUp, AlertTriangle, Volume2, Plus, Trash2,
} from 'lucide-react'
import { laborApi, pickersApi, type LaborRate } from '../lib/api'

const C = {
  bg: 'var(--color-bg)', card: 'var(--color-bg-card)', border: 'rgba(255,255,255,0.08)',
  amber: 'var(--color-primary)', green: '#2D6A2F', greenLt: '#4CAF50', red: '#E5533C',
  blue: '#5B8DEF', text: 'var(--color-text)', muted: 'var(--color-text-muted)', faint: 'var(--color-text-faint)',
}
const tierColor: Record<string, string> = {
  strong: C.greenLt, experienced: C.blue, average: C.muted, new: C.amber,
}
const usd = (n: number) => `$${(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const todayISO = () => new Date().toISOString().slice(0, 10)

export function LaborPage() {
  const qc = useQueryClient()
  const [date, setDate] = useState(todayISO())
  const inv = () => {
    qc.invalidateQueries({ queryKey: ['labor-summary'] })
    qc.invalidateQueries({ queryKey: ['labor-shifts'] })
    qc.invalidateQueries({ queryKey: ['labor-recs'] })
  }

  const summary = useQuery({ queryKey: ['labor-summary', date], queryFn: () => laborApi.summary(date).then(r => r.data) })
  const week    = useQuery({ queryKey: ['labor-week', date],    queryFn: () => laborApi.weekSummary(date).then(r => r.data) })
  const recs    = useQuery({ queryKey: ['labor-recs'],          queryFn: () => laborApi.recommendations().then(r => r.data) })
  const shifts  = useQuery({ queryKey: ['labor-shifts', date],  queryFn: () => laborApi.shifts(date).then(r => r.data.shifts) })
  const pickers = useQuery({ queryKey: ['pickers'],             queryFn: () => pickersApi.list().then(r => r.data.pickers) })
  const settings= useQuery({ queryKey: ['labor-settings'],      queryFn: () => laborApi.getSettings().then(r => r.data) })
  const rates   = useQuery({ queryKey: ['labor-rates'],         queryFn: () => laborApi.listRates().then(r => r.data.rates) })

  const clockIn  = useMutation({ mutationFn: (pid: string) => laborApi.clockIn(pid), onSuccess: inv })
  const clockOut = useMutation({ mutationFn: (id: string) => laborApi.clockOut(id), onSuccess: inv })
  const preset   = useMutation({ mutationFn: (st: string) => laborApi.applyPreset(st), onSuccess: () => qc.invalidateQueries({ queryKey: ['labor-settings'] }) })
  const minWage  = useMutation({ mutationFn: (v: number) => laborApi.updateSettings({ min_wage_usd: v }), onSuccess: () => { qc.invalidateQueries({ queryKey: ['labor-settings'] }); inv() } })
  const audio    = useMutation({ mutationFn: () => laborApi.requestAudio(date), onSuccess: inv })
  const addTally = useMutation({
    mutationFn: (b: { picker_id: string; pay_unit: string; quantity: number }) => laborApi.addTally(b),
    onSuccess: inv,
  })
  const addRate  = useMutation({
    mutationFn: (b: { pay_unit: string; rate_usd: number; crop_label?: string; bin_target_lb?: number }) => laborApi.createRate(b),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['labor-rates'] }),
  })
  const delRate  = useMutation({ mutationFn: (id: string) => laborApi.deleteRate(id), onSuccess: () => qc.invalidateQueries({ queryKey: ['labor-rates'] }) })

  const s = summary.data
  const openShifts = (shifts.data ?? []).filter(sh => sh.status === 'open')

  // Tally form state
  const [tPicker, setTPicker] = useState(''); const [tUnit, setTUnit] = useState('lb'); const [tQty, setTQty] = useState('')
  // Rate form state
  const [rUnit, setRUnit] = useState('lb'); const [rRate, setRRate] = useState(''); const [rCrop, setRCrop] = useState(''); const [rBin, setRBin] = useState('')

  return (
    <main id="main-content" style={{ padding: '28px 32px', minHeight: '100%', background: C.bg, color: C.text }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, fontFamily: '"Syne", system-ui, sans-serif' }}>Picking Labor</h1>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
            style={{ background: C.card, color: C.text, border: `1px solid ${C.border}`, borderRadius: 8, padding: '7px 10px' }} />
          <button onClick={() => audio.mutate()} title="Generate daily audio briefing"
            style={btn(C.card, C.text)}>
            <Volume2 size={14} /> {s?.audio_status === 'pending' ? 'Preparing…' : s?.audio_status === 'ready' ? 'Audio ready' : 'Audio clip'}
          </button>
        </div>
      </div>
      <p style={{ margin: '0 0 18px', fontSize: 12, color: C.faint }}>
        {settings.data ? `${settings.data.state_code} rules · minimum wage ${usd(settings.data.min_wage_usd)}/hr` : '—'}
      </p>

      {/* Stat row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 12, marginBottom: 18 }}>
        <Stat icon={Scale}      label="Picked"      value={`${(s?.total_lb ?? 0).toFixed(0)} lb`} sub={`${(s?.total_bins ?? 0).toFixed(0)} bins`} accent={C.green} />
        <Stat icon={DollarSign} label="Labor cost"  value={usd(s?.total_labor_usd ?? 0)} sub={s ? `${usd(s.piece_pay_usd)} piece` : ''} accent={C.amber} />
        <Stat icon={TrendingUp} label="$ / lb"      value={`$${(s?.cost_per_lb_usd ?? 0).toFixed(3)}`} sub="labor only" accent={C.blue} />
        <Stat icon={Clock}      label="Lb / hr"     value={(s?.avg_lb_per_hour ?? 0).toFixed(1)} sub={`${(s?.total_hours ?? 0).toFixed(1)} h`} accent={C.greenLt} />
        <Stat icon={AlertTriangle} label="Min-wage topup" value={usd(s?.topup_usd ?? 0)} sub={s && s.ot_premium_usd > 0 ? `+${usd(s.ot_premium_usd)} OT` : 'piece floor'} accent={(s?.topup_usd ?? 0) > 0 ? C.red : C.faint} />
        <Stat icon={Users}      label="Pickers"     value={String(s?.picker_count ?? 0)} sub={`${openShifts.length} clocked in`} accent={C.muted} />
      </div>

      {/* Narrative */}
      {s?.narrative && (
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5 }}>{s.narrative}</p>
            <span style={{ ...chip(C.faint), flexShrink: 0, marginLeft: 12 }}>{s.narrative_source === 'vertex' ? 'auto' : 'basic'}</span>
          </div>
        </Card>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 18 }}>
        {/* Per-picker wages */}
        <Card title="Per-picker pay">
          {(s?.pickers?.length ?? 0) === 0 ? <Empty msg="No picking logged for this day yet." /> : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr style={{ color: C.muted, textAlign: 'left', fontSize: 11 }}>
                <th style={th}>Picker</th><th style={thR}>Lbs</th><th style={thR}>Hrs</th><th style={thR}>Lb/hr</th><th style={thR}>Piece</th><th style={thR}>Topup</th><th style={thR}>Total</th>
              </tr></thead>
              <tbody>
                {s!.pickers.map(p => (
                  <tr key={p.picker_id} style={{ borderTop: `1px solid ${C.border}` }}>
                    <td style={td}>{p.picker_name || '—'} {p.below_minimum && <span style={chip(C.red)}>topped up</span>}</td>
                    <td style={tdR}>{p.lbs.toFixed(0)}</td>
                    <td style={tdR}>{p.hours.toFixed(1)}</td>
                    <td style={tdR}>{p.lbs_per_hour.toFixed(1)}</td>
                    <td style={tdR}>{usd(p.piece_pay_usd)}</td>
                    <td style={{ ...tdR, color: p.topup_usd > 0 ? C.red : C.faint }}>{p.topup_usd > 0 ? usd(p.topup_usd) : '—'}</td>
                    <td style={{ ...tdR, fontWeight: 600 }}>{usd(p.total_pay_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        {/* Crew recommendations */}
        <Card title="Crew recommendations">
          {recs.data?.narrative && (
            <p style={{ margin: '0 0 12px', fontSize: 13, lineHeight: 1.5, color: C.text }}>
              {recs.data.narrative}
              <span style={{ ...chip(C.faint), marginLeft: 8 }}>{recs.data.source === 'vertex' ? 'auto' : 'basic'}</span>
            </p>
          )}
          {(recs.data?.pairings?.length ?? 0) > 0 && (
            <div style={{ marginBottom: 12 }}>
              {recs.data!.pairings.map((p, i) => (
                <div key={i} style={{ fontSize: 13, padding: '6px 0', borderTop: i ? `1px solid ${C.border}` : 'none' }}>
                  <strong style={{ color: C.greenLt }}>{p.mentor_name}</strong> + <strong style={{ color: C.amber }}>{p.mentee_name}</strong>
                  <div style={{ fontSize: 11, color: C.faint }}>{p.reason}</div>
                </div>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {(recs.data?.picker_stats ?? []).map(st => (
              <span key={st.picker_id} style={chip(tierColor[st.tier] ?? C.muted)}>
                {st.picker_name} · {st.lbs_per_hour.toFixed(1)} lb/hr · {st.tier}
              </span>
            ))}
          </div>
        </Card>
      </div>

      {/* Weekly overtime (FLSA 40h) */}
      {(() => {
        const w = week.data
        const otRows = (w?.pickers ?? []).filter(p => p.hours > 0)
        return (
          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
              <h2 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>
                Weekly overtime <span style={{ color: C.faint, fontWeight: 400 }}>(FLSA {w?.ot_threshold_hours ?? 40}h/week)</span>
              </h2>
              {w && <span style={{ fontSize: 12, color: C.muted }}>Week of {w.week_start} – {w.week_end}</span>}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 14 }}>
              <Stat icon={Clock}      label="Week hours"   value={(w?.total_hours ?? 0).toFixed(1)} sub="all pickers" accent={C.blue} />
              <Stat icon={AlertTriangle} label="Weekly OT hrs" value={(w?.total_weekly_ot_hours ?? 0).toFixed(1)} sub={w?.has_daily_ot ? 'beyond daily OT' : 'over threshold'} accent={(w?.total_weekly_ot_hours ?? 0) > 0 ? C.amber : C.faint} />
              <Stat icon={DollarSign} label="OT premium"   value={usd(w?.total_weekly_ot_premium_usd ?? 0)} sub="estimate" accent={(w?.total_weekly_ot_premium_usd ?? 0) > 0 ? C.red : C.faint} />
            </div>
            {otRows.length === 0 ? <Empty msg="No shifts logged for this week yet." /> : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr style={{ color: C.muted, textAlign: 'left', fontSize: 11 }}>
                  <th style={th}>Picker</th><th style={thR}>Week hrs</th>
                  {w?.has_daily_ot && <th style={thR}>Daily OT</th>}
                  <th style={thR}>Weekly OT</th><th style={thR}>Reg rate</th><th style={thR}>OT premium</th>
                </tr></thead>
                <tbody>
                  {otRows.map(p => (
                    <tr key={p.picker_id} style={{ borderTop: `1px solid ${C.border}` }}>
                      <td style={td}>{p.picker_name || '—'}</td>
                      <td style={tdR}>{p.hours.toFixed(1)}</td>
                      {w?.has_daily_ot && <td style={{ ...tdR, color: C.faint }}>{p.daily_ot_hours > 0 ? p.daily_ot_hours.toFixed(1) : '—'}</td>}
                      <td style={{ ...tdR, color: p.weekly_ot_hours > 0 ? C.amber : C.faint }}>{p.weekly_ot_hours > 0 ? p.weekly_ot_hours.toFixed(1) : '—'}</td>
                      <td style={tdR}>{usd(p.regular_rate_usd)}</td>
                      <td style={{ ...tdR, fontWeight: 600, color: p.weekly_ot_premium_usd > 0 ? C.red : C.faint }}>{p.weekly_ot_premium_usd > 0 ? usd(p.weekly_ot_premium_usd) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <p style={{ fontSize: 11, color: C.faint, marginTop: 10, lineHeight: 1.5 }}>
              FLSA pays overtime on hours over {w?.ot_threshold_hours ?? 40} in the workweek.
              {w?.has_daily_ot ? ' Hours already paid as daily overtime are excluded here so they aren’t counted twice.' : ''}
              {' '}Estimate for review — confirm with your payroll provider.
            </p>
          </Card>
        )
      })()}

      {/* Clock board + tally entry */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginTop: 18 }}>
        <Card title="Clock board">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
            {(pickers.data ?? []).filter(p => p.is_active).map(p => (
              <button key={p.id} disabled={clockIn.isPending} onClick={() => clockIn.mutate(p.id)} style={btn(C.card, C.text)}>
                <Clock size={13} /> {p.name}
              </button>
            ))}
            {(pickers.data?.length ?? 0) === 0 && <Empty msg="No pickers yet — add crew in Picking." />}
          </div>
          {openShifts.map(sh => (
            <div key={sh.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderTop: `1px solid ${C.border}`, fontSize: 13 }}>
              <span>{sh.picker_name || sh.picker_id.slice(0, 8)} <span style={{ color: C.faint }}>· in {sh.clock_in_at ? new Date(sh.clock_in_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}</span></span>
              <button onClick={() => clockOut.mutate(sh.id)} style={btn(C.green, '#fff')}>Clock out</button>
            </div>
          ))}
        </Card>

        <Card title="Record a tally">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <select value={tPicker} onChange={e => setTPicker(e.target.value)} style={input}>
              <option value="">Picker…</option>
              {(pickers.data ?? []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <select value={tUnit} onChange={e => setTUnit(e.target.value)} style={input}>
              <option value="lb">lbs</option><option value="bin">bins</option>
            </select>
            <input type="number" placeholder="qty" value={tQty} onChange={e => setTQty(e.target.value)} style={{ ...input, width: 80 }} />
            <button disabled={!tPicker || !tQty || addTally.isPending}
              onClick={() => { addTally.mutate({ picker_id: tPicker, pay_unit: tUnit, quantity: Number(tQty) }); setTQty('') }}
              style={btn(C.amber, '#1a1300')}><Plus size={13} /> Add</button>
          </div>
          <p style={{ fontSize: 11, color: C.faint, marginTop: 10 }}>Tallies feed piece pay and lbs/hr. Bins convert to lbs via each crop's rate target.</p>
        </Card>
      </div>

      {/* Settings + rates */}
      <Card title="Pay configuration">
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
          <label style={{ fontSize: 13 }}>Min wage $/hr&nbsp;
            <input type="number" step="0.25" defaultValue={settings.data?.min_wage_usd}
              onBlur={e => { const v = Number(e.target.value); if (v > 0 && v !== settings.data?.min_wage_usd) minWage.mutate(v) }}
              style={{ ...input, width: 80 }} />
          </label>
          <span style={{ fontSize: 13, color: C.muted }}>Rules:</span>
          {['FL', 'CA'].map(st => (
            <button key={st} onClick={() => preset.mutate(st)}
              style={btn(settings.data?.state_code === st ? C.green : C.card, settings.data?.state_code === st ? '#fff' : C.text)}>{st}</button>
          ))}
          {settings.data?.piece_rate_rest_breaks && <span style={chip(C.blue)}>CA rest-break pay on</span>}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
          <input placeholder="crop (optional)" value={rCrop} onChange={e => setRCrop(e.target.value)} style={{ ...input, width: 130 }} />
          <select value={rUnit} onChange={e => setRUnit(e.target.value)} style={input}><option value="lb">$/lb</option><option value="bin">$/bin</option></select>
          <input type="number" step="0.01" placeholder="rate" value={rRate} onChange={e => setRRate(e.target.value)} style={{ ...input, width: 90 }} />
          {rUnit === 'bin' && <input type="number" placeholder="lb/bin" value={rBin} onChange={e => setRBin(e.target.value)} style={{ ...input, width: 90 }} />}
          <button disabled={!rRate || addRate.isPending}
            onClick={() => { addRate.mutate({ pay_unit: rUnit, rate_usd: Number(rRate), crop_label: rCrop || undefined, bin_target_lb: rBin ? Number(rBin) : undefined }); setRRate(''); setRCrop(''); setRBin('') }}
            style={btn(C.amber, '#1a1300')}><Plus size={13} /> Add rate</button>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {(rates.data ?? []).map((r: LaborRate) => (
            <span key={r.id} style={{ ...chip(C.card), border: `1px solid ${C.border}`, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {r.crop_label || 'default'} · {usd(r.rate_usd)}/{r.pay_unit}
              <Trash2 size={12} style={{ cursor: 'pointer', color: C.faint }} onClick={() => delRate.mutate(r.id)} />
            </span>
          ))}
        </div>
      </Card>

      <p style={{ fontSize: 11, color: C.faint, marginTop: 16, lineHeight: 1.5 }}>
        {s?.disclaimer ?? 'Estimate to assist crew planning — not payroll or legal advice. Verify final wages with your payroll provider.'}
      </p>
    </main>
  )
}

// ── bits ────────────────────────────────────────────────────────────────────
function Stat({ icon: Icon, label, value, sub, accent }: { icon: React.ElementType; label: string; value: string; sub?: string; accent: string }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '12px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: C.muted, fontSize: 11 }}><Icon size={13} color={accent} /> {label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, marginTop: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: C.faint }}>{sub}</div>}
    </div>
  )
}
function Card({ title, children }: { title?: string; children: React.ReactNode }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 18, marginBottom: 18 }}>
      {title && <h2 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600 }}>{title}</h2>}
      {children}
    </div>
  )
}
function Empty({ msg }: { msg: string }) { return <p style={{ color: C.faint, fontSize: 13, margin: 0 }}>{msg}</p> }

const btn = (bg: string, fg: string): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', gap: 5, background: bg, color: fg,
  border: `1px solid ${C.border}`, borderRadius: 8, padding: '6px 11px', fontSize: 12, fontWeight: 500, cursor: 'pointer',
})
const chip = (color: string): React.CSSProperties => ({
  display: 'inline-block', fontSize: 10, fontWeight: 600, color, background: 'var(--color-bg-hover)',
  border: `1px solid ${color}33`, borderRadius: 100, padding: '2px 8px', marginLeft: 4,
})
const input: React.CSSProperties = { background: C.bg, color: C.text, border: `1px solid ${C.border}`, borderRadius: 8, padding: '6px 9px', fontSize: 13 }
const th: React.CSSProperties = { padding: '4px 6px', fontWeight: 600 }
const thR: React.CSSProperties = { ...th, textAlign: 'right' }
const td: React.CSSProperties = { padding: '7px 6px' }
const tdR: React.CSSProperties = { ...td, textAlign: 'right' }

export default LaborPage
